// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useRef, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import type { TyClient } from "@/types";
import { makeThumbnail } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import DateTimeInput from "@/UI/inputs/DateTimeInput";
import SelectInput from "@/UI/inputs/SelectInput";
import TextInput from "@/UI/inputs/TextInput";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyEditProfileDetails = { onDone?: () => void };

type Props = { onClose: () => void; details?: unknown };

/**
 * A person's own details. **Not the phone number** — it is the identity this account signs in with,
 * and changing it is an admin's job for the same reason a password reset is. It is shown anyway,
 * disabled, because a form that silently drops the one field somebody came looking for reads as a
 * bug rather than a rule.
 *
 * **The picture does not go through the drive.** A client is one record across every company they
 * work in, so filing their face under whichever one they happened to be looking at would bill that
 * company's storage for it and lose it the day they leave. It uploads straight to
 * `public/avatars/{clientId}` instead — sign, `PUT`, confirm, exactly the drive's two steps with
 * the tenant taken out.
 *
 * It is downscaled here before it is sent. A phone camera's eight megapixels is a second of waiting
 * for something drawn at 46 pixels, and the browser already has the bytes in hand.
 */
export default function EditProfilePopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { onDone } = (details ?? {}) as TyEditProfileDetails;

    const client = useAuth((state) => state.client);
    const updateClient = useAuth((state) => state.updateClient);
    const token = useAuth((state) => state.token);

    const [name, setName] = useState(client?.name ?? "");
    const [email, setEmail] = useState(client?.email ?? "");
    const [gender, setGender] = useState(client?.gender ?? "");
    const [birthDate, setBirthDate] = useState<string | null>(client?.birthDate ?? null);
    /** What the avatar shows while the bytes are still going up — the chosen file, read locally. */
    const [preview, setPreview] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    const save = useFetch<{ client: TyClient }>("/auth/me", { method: "PATCH" }, false);
    const picture = useFetch<{ client: TyClient }>("/auth/picture", {}, false);

    /** Both calls answer with the whole account, so whichever returns last is the truth. */
    const returned = save.data?.client ?? null;
    useEffect(() => {
        if (!save.succeededAt || !returned) return;
        updateClient(returned);
        onDone?.();
        onClose();
    }, [save.succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    const changed = picture.data?.client ?? null;
    useEffect(() => {
        if (!picture.succeededAt || !changed) return;
        updateClient(changed);
        onDone?.();
    }, [picture.succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    // The object URL outlives the element that used it, so it is handed back when this closes.
    useEffect(
        () => () => {
            if (preview) URL.revokeObjectURL(preview);
        },
        [preview]
    );

    /**
     * Sign, put, confirm. It is written out here rather than going through `useUploads` because the
     * tray is the drive's — it writes `File` rows against an organization, which is the one thing
     * an avatar must not do.
     */
    const handlePick = async (file: File) => {
        setUploadError(null);
        setUploading(true);
        try {
            const shrunk = (await makeThumbnail(file)) ?? file;
            const sent = new File([shrunk], `avatar.${constants.thumbnail.mimeType.split("/")[1]}`, { type: shrunk.type || file.type });
            setPreview(URL.createObjectURL(sent));

            const headers: Record<string, string> = { "Content-Type": "application/json" };
            if (token) headers.Authorization = `Bearer ${token}`;

            const signed = await fetch(`${constants.url}/auth/picture`, {
                method: "POST",
                headers,
                body: JSON.stringify({ name: sent.name, mimeType: sent.type }),
            });
            const payload = await signed.json();
            if (!signed.ok || !payload.success) throw new Error(payload.message);

            // Straight to S3 against the signature. Our server never sees the bytes.
            const landed = await fetch(payload.data.uploadUrl, { method: "PUT", headers: { "Content-Type": sent.type }, body: sent });
            if (!landed.ok) throw new Error();

            picture.invoke({ method: "PATCH", data: { key: payload.data.key } });
        } catch {
            setPreview(null);
            setUploadError(t.profile.uploadFailed);
        } finally {
            setUploading(false);
        }
    };

    const busy = save.loading || picture.loading || uploading;
    /**
     * The local file stands in only while the bytes are in the air. Once the server has answered,
     * `client.picture` **is** the new face — so the preview is dropped by being ignored rather than
     * by clearing state from an effect, which is a cascading render for no gain.
     */
    const inFlight = uploading || picture.loading;
    const shown = (inFlight ? preview : null) ?? client?.picture ?? null;

    const handleSubmit = () => {
        if (busy || !name.trim()) return;
        save.invoke({
            method: "PATCH",
            data: { name: name.trim(), email: email.trim() || null, gender: gender || null, birthDate },
        });
    };

    return (
        <section className="side-form side-form--center edit-profile">
            <header className="side-form__header">
                <h2 className="side-form__title">{t.profile.editProfile}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                {/* The face first, and it saves on its own: an upload is a finished action, not a
                    field waiting for the form's button. */}
                <div className="edit-profile__picture">
                    <Avatar name={name || client?.name || ""} picture={shown} size={74} bordered />

                    <div className="edit-profile__picture-side">
                        <span className="edit-profile__picture-label">{t.profile.picture}</span>
                        <span className="edit-profile__picture-hint">{t.profile.pictureHint}</span>

                        <div className="edit-profile__picture-actions">
                            <button type="button" className="edit-profile__picture-btn" onClick={() => fileRef.current?.click()} disabled={busy}>
                                <Icon name="upload" size={14} />
                                <span>{t.profile.changePicture}</span>
                            </button>
                            {client?.picture && (
                                <button
                                    type="button"
                                    className="edit-profile__picture-btn edit-profile__picture-btn--quiet"
                                    onClick={() => picture.invoke({ method: "DELETE" })}
                                    disabled={busy}
                                >
                                    <Icon name="trash" size={14} />
                                    <span>{t.profile.removePicture}</span>
                                </button>
                            )}
                        </div>
                    </div>

                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        className="edit-profile__file"
                        onChange={(event) => {
                            const file = event.target.files?.[0];
                            event.target.value = "";
                            if (file) handlePick(file);
                        }}
                    />
                </div>

                <TextInput value={name} onChange={setName} label={t.profile.fields.name} disabled={busy} />
                <TextInput value={email} onChange={setEmail} label={t.profile.fields.email} type="email" dir="ltr" disabled={busy} />

                <SelectInput
                    value={gender}
                    onChange={setGender}
                    label={t.profile.fields.gender}
                    options={[
                        { value: "", label: t.profile.genders.unset },
                        ...constants.genders.map((item) => ({ value: item, label: t.profile.genders[item] })),
                    ]}
                    disabled={busy}
                />

                <DateTimeInput
                    value={birthDate}
                    onChange={setBirthDate}
                    label={t.profile.fields.birthDate}
                    dateOnly
                    max={new Date()}
                    disabled={busy}
                />

                {/* Disabled rather than missing: the rule is easier to accept when you can see it. */}
                <TextInput value={client?.phone ?? ""} onChange={() => {}} label={t.profile.fields.phone} dir="ltr" disabled />
                <p className="side-form__hint">{t.profile.phoneHint}</p>

                {(uploadError || picture.error || save.error) && <p className="side-form__error">{uploadError ?? picture.error ?? save.error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={busy}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={busy || !name.trim()}>
                    {save.loading ? t.common.loading : t.common.save}
                </button>
            </footer>
        </section>
    );
}
